import swal from 'sweetalert2'

import { Box, Button, DashboardModule } from '@domain-group/suit-theme'

import type { ValidateOrderItemError } from '@functions/orderItem'

import useOrderItem from '@hooks/useOrderItem'

import type { UseOrderApprovalPayload } from '@modals/ApproveOrderModal/hooks/useOrderApproval'

import Errors from '../Errors'

import DeliveryAddress from './DeliveryAddress'
import Notes from './Notes'
import PurchaseOrder from './PurchaseOrder'
import Total from './Total'

type ConfirmOrderApprovalParams = {
  processFn: VoidFunction,
}

const confirmOrderApproval = (params: ConfirmOrderApprovalParams) => {
  const { processFn } = params

  swal
    .fire({
      confirmButtonColor: '#43AC6A',
      confirmButtonText: 'Yes, approve it.',
      icon: 'success',
      showCancelButton: true,
      text: 'Are you sure? All orders will be placed with your provider and cannot be reversed.',
      title: 'Approve Order?',
    })
    .then(({ value }) => {
      if (value){
        processFn()
      }
    })
}

type RenderApproveButtonParams = {
  acceptedDisclaimerIds: number[],
  approving: boolean,
  blockers: ValidateOrderItemError[],
  callbacks: {
    approveOrder: VoidFunction,
  },
  cashPaymentRequired: boolean,
  disclaimersAccepted: boolean,
  hasErrors: boolean,
  hasOrderDisclaimers: boolean,
  isApprovable: boolean,
  orderDisclaimers: {
    ids: number[],
    notRequiredIds: number[],
  },
  payAndApproveOrder: VoidFunction,
}

const renderApproveButton = (params: RenderApproveButtonParams) => {
  const {
    acceptedDisclaimerIds,
    approving,
    blockers,
    callbacks: {
      approveOrder,
    },
    cashPaymentRequired,
    disclaimersAccepted,
    hasErrors,
    hasOrderDisclaimers,
    isApprovable,
    orderDisclaimers: { ids, notRequiredIds },
    payAndApproveOrder,
  } = params

  if (hasErrors){
    return (
      <Button buttonStyle="primaryCreate" disabled size="large" width="100%">
        {blockers.length === 1 ? '1 Error' : `${blockers.length} Errors`}
      </Button>
    )
  }

  // Disclaimer Errors
  if (hasOrderDisclaimers && !disclaimersAccepted){
    const label = hasOrderDisclaimers
      ? `Disclaimers (${acceptedDisclaimerIds.length + notRequiredIds.length + 1}/${ids.length + 1} Accepted)`
      : 'Disclaimers (1/1 Accepted)'

    return (
      <Button buttonStyle="primaryCreate" disabled size="large" width="100%">
        {label}
      </Button>
    )
  }

  // Account is locked or cash only
  if (cashPaymentRequired){
    return (
      <Button
        buttonStyle="primaryCreate"
        disabled={!isApprovable}
        loading={approving}
        loadingBubbleColor="white"
        onClick={payAndApproveOrder}
        size="large"
        width="100%"
      >
        Pay & Confirm
      </Button>
    )
  }

  return (
    <Button
      buttonStyle="primaryCreate"
      disabled={!isApprovable}
      loading={approving}
      loadingBubbleColor="white"
      onClick={() => confirmOrderApproval({ processFn: approveOrder })}
      size="large"
      width="100%"
    >
      Confirm Order
    </Button>
  )
}

type OrderDetailsProps = {
  callbacks: {
    approveOrder: VoidFunction,
  },
  orderApprovalPayload: UseOrderApprovalPayload,
}

const OrderDetails = (props: OrderDetailsProps) => {
  const {
    callbacks,
    orderApprovalPayload,
    orderApprovalPayload: {
      acceptedDisclaimerIds,
      approving,
      cashPaymentRequired,
      disclaimersAccepted,
      errors,
      errors: { blockers },
      firstOrderItem,
      hasOrderDisclaimers,
      isApprovable,
      order: {
        items,
      },
      orderDisclaimers,
    },
  } = props

  const orderItemPayload = useOrderItem(firstOrderItem)
  const {
    callbacks: {
      updateOrderItem: updateOrderItemFn,
    },
    isNotable,
  } = orderItemPayload

  const hasErrors = !!blockers?.length

  return (
    <DashboardModule boxProps={{ position: ['relative', 'sticky'], top: 0 }} title="Confirm Order">
      <Box flexDirection="column">
        <Box flexDirection="column" padding="large">
          <PurchaseOrder orderApprovalPayload={orderApprovalPayload} />

          {items?.length === 1 && isNotable && (
            <Notes orderItem={firstOrderItem} callbacks={{ updateOrderItemFn }} />
          )}

          <DeliveryAddress orderApprovalPayload={orderApprovalPayload} />

          <Total orderApprovalPayload={orderApprovalPayload} />

          <Errors errors={errors.combined} />
        </Box>

        <Box borderTop="1px solid" borderColor="lineColor" padding="large">
          {renderApproveButton({
            acceptedDisclaimerIds,
            approving,
            // @ts-expect-error TS2322
            blockers,
            callbacks,
            cashPaymentRequired,
            disclaimersAccepted,
            hasErrors,
            hasOrderDisclaimers,
            isApprovable,
            orderDisclaimers,
          })}
        </Box>
      </Box>
    </DashboardModule>
  )
}

export default OrderDetails
